import { Megaphone } from "lucide-react";

// Channel → colour. Keys match the backend `_classify_source` labels + "Office".
export const CHANNEL_META = {
  "Google Search": { cls: "bg-[#E3EDF7] text-[#1D4ED8] border-[#C4D9EE]" },
  Search: { cls: "bg-[#E3EDF7] text-[#2E6BB0] border-[#C4D9EE]" },
  Ads: { cls: "bg-[#FBE3D6] text-[#8A3D0A] border-[#F0C9B0]" },
  Social: { cls: "bg-[#F1E4F7] text-[#6B21A8] border-[#E0C9EE]" },
  Email: { cls: "bg-[#FBF3EC] text-[#8A6D2B] border-[#F0D5B4]" },
  Campaign: { cls: "bg-[#FBF3EC] text-[#A87E28] border-[#F0D5B4]" },
  Referral: { cls: "bg-[#D8EED8] text-[#1F6B3B] border-[#BFE0BF]" },
  Direct: { cls: "bg-[#F0EAE0] text-[#6B7280] border-[#E5E7EB]" },
  Office: { cls: "bg-[#12263A] text-white border-[#12263A]" },
};

const slug = (s) => String(s || "unknown").toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function leadSourceTip(ls) {
  if (!ls) return "";
  const bits = [];
  if (ls.utm_source) bits.push(`utm_source: ${ls.utm_source}`);
  if (ls.utm_medium) bits.push(`utm_medium: ${ls.utm_medium}`);
  if (ls.utm_campaign) bits.push(`campaign: ${ls.utm_campaign}`);
  if (ls.referrer_host) bits.push(`referrer: ${ls.referrer_host}`);
  if (ls.landing_path) bits.push(`landed on: ${ls.landing_path}`);
  if (ls.channel === "Office") bits.push("Built by staff in the configurator");
  return bits.length ? bits.join("\n") : "No campaign details — visitor arrived directly";
}

// Small pill shown on each quote row / quote file: which marketing channel the client came from.
export function LeadSourcePill({ source, rowKey }) {
  if (!source?.channel) return null;
  const meta = CHANNEL_META[source.channel] || CHANNEL_META.Direct;
  const detail = source.utm_campaign || source.referrer_host || "";
  return (
    <span data-testid={`lead-source-${rowKey || slug(source.channel)}`} title={leadSourceTip(source)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-widest border ${meta.cls}`}>
      <Megaphone size={9} /> {source.channel}{detail ? <span className="font-bold normal-case tracking-normal opacity-80">· {detail}</span> : null}
    </span>
  );
}

// Compact "where do quotes come from" strip — click a channel to filter the list.
export function LeadSourceSummary({ rows, active, onPick }) {
  const counts = {};
  for (const r of rows) {
    const ch = r.lead_source?.channel;
    if (ch) counts[ch] = (counts[ch] || 0) + 1;
  }
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  const tracked = entries.reduce((a, [, n]) => a + n, 0);
  return (
    <div data-testid="lead-source-summary" className="flex flex-wrap items-center gap-1.5 mb-3">
      <span className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold inline-flex items-center gap-1 mr-1">
        <Megaphone size={11} className="text-[#C79A3B]" /> Lead sources · {tracked} tracked
      </span>
      {entries.map(([ch, n]) => {
        const meta = CHANNEL_META[ch] || CHANNEL_META.Direct;
        const on = active === ch;
        return (
          <button key={ch} type="button" data-testid={`lead-source-filter-${slug(ch)}`}
            onClick={() => onPick(on ? null : ch)}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-black uppercase tracking-widest border transition-colors ${meta.cls} ${on ? "ring-2 ring-[#C79A3B] ring-offset-1" : "hover:opacity-80"}`}>
            {ch} <span className="normal-case tracking-normal font-bold opacity-80">{n} · {Math.round(n / tracked * 100)}%</span>
          </button>
        );
      })}
      {active && (
        <button type="button" data-testid="lead-source-filter-clear" onClick={() => onPick(null)}
          className="text-[10px] font-bold text-[#6B7280] hover:text-[#12263A] ml-1">Clear ✕</button>
      )}
    </div>
  );
}
